/**
 * Kritik uyarı sinyalleri: işletim sistemi bildirimi ve uyarı sesi.
 *
 * Bot gece de çalışıyor; kullanıcı uygulamaya bakmıyorken tek yakalayıcı bunlar.
 * Dialog yalnızca uygulama önplandayken işe yarar.
 *
 * Ses dosyası paketlenmiyor: Web Audio ile üretiliyor. Böylece asset yönetimi,
 * CSP ve dosya yolu sorunu olmuyor.
 */

/** Electron renderer'da Notification doğrudan Windows/macOS bildirimine dönüşür. */
export function isletimSistemiBildirimi(baslik: string, metin: string): void {
    try {
        if (typeof Notification === 'undefined') {
            return;
        }
        if (Notification.permission === 'granted') {
            new Notification(baslik, {body: metin});
            return;
        }
        if (Notification.permission !== 'denied') {
            Notification.requestPermission().then((izin) => {
                if (izin === 'granted') new Notification(baslik, {body: metin});
            });
        }
    } catch (e) {
        console.warn('İşletim sistemi bildirimi gösterilemedi:', e);
    }
}

interface Nota {
    /** Hz */
    frekans: number;
    /** Sekansın başından itibaren saniye */
    baslangic: number;
    /** Saniye */
    sure: number;
    tip?: OscillatorType;
    /** 0-1 arası tepe seviye */
    ses?: number;
}

/**
 * Nota dizisini çalar.
 *
 * Zarf sıfırdan başlayıp sıfıra iner; aksi halde başlangıç/bitişte tıkırtı olur.
 * exponentialRamp sıfır kabul etmediği için 0.0001 kullanılıyor.
 */
function calSekans(notalar: Nota[]): void {
    try {
        const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtx || !notalar.length) {
            return;
        }
        const ctx = new AudioCtx();
        const t0 = ctx.currentTime;

        notalar.forEach((n) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = n.tip ?? 'sine';
            osc.frequency.value = n.frekans;

            const bas = t0 + n.baslangic;
            const tepe = Math.max(0.01, Math.min(1, n.ses ?? 0.25));
            const atak = Math.min(0.02, n.sure * 0.2);

            gain.gain.setValueAtTime(0.0001, bas);
            gain.gain.exponentialRampToValueAtTime(tepe, bas + atak);
            gain.gain.exponentialRampToValueAtTime(0.0001, bas + n.sure);

            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(bas);
            osc.stop(bas + n.sure + 0.02);
        });

        const toplamSure = Math.max(...notalar.map((n) => n.baslangic + n.sure));
        window.setTimeout(() => ctx.close().catch(() => undefined), (toplamSure + 0.3) * 1000);
    } catch (e) {
        console.warn('Uyarı sesi çalınamadı:', e);
    }
}

export type SesSecenegi =
    | 'ikiNota'
    | 'can'
    | 'yukselen'
    | 'ciftBip'
    | 'alcalan'
    | 'yumusak';

export const SES_SECENEKLERI: Array<{ id: SesSecenegi, ad: string, aciklama: string }> = [
    {id: 'ikiNota', ad: 'İki nota', aciklama: 'Kısa iniş — dikkat çeker, rahatsız etmez'},
    {id: 'can', ad: 'Çan', aciklama: 'Uzun sönümlü, yumuşak tını'},
    {id: 'yukselen', ad: 'Yükselen', aciklama: 'Üç nota, klasik bildirim hissi'},
    {id: 'ciftBip', ad: 'Çift bip', aciklama: 'Sistem uyarısı, en net'},
    {id: 'alcalan', ad: 'Alçalan', aciklama: 'Sorun/hata çağrışımı güçlü'},
    {id: 'yumusak', ad: 'Yumuşak', aciklama: 'Tek ton, en az müdahaleci'}
];

const SEKANSLAR: Record<SesSecenegi, Nota[]> = {
    // 880 -> 660: mevcut varsayılan.
    ikiNota: [
        {frekans: 880, baslangic: 0, sure: 0.16},
        {frekans: 660, baslangic: 0.18, sure: 0.16}
    ],

    // Temel + iki harmonik, uzun sönüm. Çan/gong hissi.
    can: [
        {frekans: 784, baslangic: 0, sure: 1.1, ses: 0.22},
        {frekans: 1568, baslangic: 0, sure: 0.55, ses: 0.08},
        {frekans: 2352, baslangic: 0, sure: 0.3, ses: 0.04}
    ],

    // Do-Mi-Sol arpej. En "bildirim" gibi olanı.
    yukselen: [
        {frekans: 523, baslangic: 0, sure: 0.12},
        {frekans: 659, baslangic: 0.11, sure: 0.12},
        {frekans: 784, baslangic: 0.22, sure: 0.26}
    ],

    // Aynı frekansta iki kısa bip. Kare dalga: en dikkat çekici.
    ciftBip: [
        {frekans: 1046, baslangic: 0, sure: 0.09, tip: 'square', ses: 0.16},
        {frekans: 1046, baslangic: 0.16, sure: 0.09, tip: 'square', ses: 0.16}
    ],

    // Üç nota iniş. Hata/sorun çağrışımı.
    alcalan: [
        {frekans: 784, baslangic: 0, sure: 0.13},
        {frekans: 622, baslangic: 0.13, sure: 0.13},
        {frekans: 466, baslangic: 0.26, sure: 0.3}
    ],

    // Tek üçgen dalga, yavaş sönüm. Gece çalışırken en az rahatsız eden.
    yumusak: [
        {frekans: 587, baslangic: 0, sure: 0.5, tip: 'triangle', ses: 0.2}
    ]
};

/** Kritik uyarı sesi (oturum sınırı vb.). */
export const VARSAYILAN_SES: SesSecenegi = 'alcalan';
/** Ana görev tamamlandığında çalan ses. */
export const GOREV_SESI: SesSecenegi = 'yukselen';

const AYAR_ANAHTARI = 'bildirimSesi';

/** Bildirim sesleri açık mı. Kullanıcı Ayarlar menüsünden kapatabilir. */
export function bildirimSesiAcikMi(): boolean {
    try {
        return localStorage.getItem(AYAR_ANAHTARI) !== 'kapali';
    } catch (e) {
        return true;
    }
}

export function bildirimSesiniAyarla(acik: boolean): void {
    try {
        localStorage.setItem(AYAR_ANAHTARI, acik ? 'acik' : 'kapali');
    } catch (e) {
        console.warn('Bildirim sesi ayarı kaydedilemedi:', e);
    }
}

/**
 * Uyarı sesi çalar. Yalnızca KRİTİK durumlarda kullanılmalı;
 * her uyarıda çalarsa kullanıcı kanıksar ve sinyal değerini yitirir.
 */
export function uyariSesi(secenek: SesSecenegi = VARSAYILAN_SES): void {
    if (!bildirimSesiAcikMi()) {
        return;
    }
    calSekans(SEKANSLAR[secenek] ?? SEKANSLAR[VARSAYILAN_SES]);
}

/** Ayar dinlemeden çalar — yalnızca ayar ekranındaki önizleme için. */
export function sesOnizle(secenek: SesSecenegi): void {
    calSekans(SEKANSLAR[secenek] ?? SEKANSLAR[VARSAYILAN_SES]);
}

/** Ana görev tamamlandığında. */
export function gorevTamamlandiSesi(): void {
    uyariSesi(GOREV_SESI);
}
